import axiosClient from 'api/axiosClient';
import store from 'app/store';
import AppConfigs from 'general/constants/AppConfigs';
import _ from 'lodash';
import { AppDialogHelper } from 'general/components/AppDialog';
import ToastHelper from 'general/helpers/ToastHelper';
import AppResource from 'general/constants/AppResource';
import { AppCustomDialogHelper } from 'general/components/AppDialogCustom';
import UIHelper from 'general/helpers/UIHelper';
import AppColors from 'general/constants/AppColors';
import Utils from 'general/utils/Utils';
import AgencyHelper, { AgencyConstants } from 'features/Agency/agencyHelper';
import ChangeFleetPasswordView from './FleetAuth/components/ChangeFleetPasswordView';
import AgencySaleDialogView from 'features/Agency/components/AgencySaleDialogView';
import FleetCreateContractView from './FleetContract/components/FleetCreateContractView';
import { dateFilter, getDateFilterOption } from 'general/components/Others/CustomDateRangePicker';
import DialogFleetReconciliationFeedback from './FleetReconciliationReport/components/DialogFleetReconciliationFeedback';
import DialogCreateFleetReconciliation from './FleetReconciliationReport/components/DialogCreateFleetReconciliation';
import DialogAddScheduleMaintenanceView from 'features/Charge/ChargePoint/components/DialogAddScheduleMaintenanceView';
import DialogTopUpFleetView from './FleetInfo/components/DialogTopUpFleetView';
import DialogSetDriverChargingPriceView from './FleetInfo/components/DialogSetDriverChargingPriceView';

// MARK: ---- CONSTANTS ----
export const FleetConstants = {
    // trang thai DNVT
    FleetStatus: {
        NORMAL: 'NORMAL',
        SIGNED: 'SIGNED',
        SUSPENDED: 'SUSPENDED',
        DELETED: 'DELETED',
        REGISTERED: 'REGISTERED',
    },
    // loai nguoi tao
    FleetCreatedBy: {
        ME: 'ME',               // Tài khoản của bạn
        OTHERS: 'OTHERS',       // Người khác
        SELF: 'SELF',           // Tự đăng ký
    },
    // trang thai thanh toan
    PaymentStatus: {
        PENDING: 'PENDING',         // Chưa nghiệm thu
        PROCESSING: 'PROCESSING',   // Chưa thanh toán
        COMPLETED: 'COMPLETED',     // Đã thanh toán
    },
    // loai DNVT
    FleetType: {
        INDIVIDUAL: 'INDIVIDUAL',     // Cá nhân
        ORGANIZATION: 'ORGANIZATION', // Tổ chức
    },
    // chinh sach thanh toan phi sac
    PaymentMethod: {
        FLEET: 'FLEET',     // Doanh nghiệp thanh toán
        WALLET: 'WALLET',   // Tài xế thanh toán
    },
    // loai gia sac
    PricingType: {
        STANDARD: 'STANDARD', // Tiêu chuẩn
        DISCOUNT: 'DISCOUNT', // Ưu đãi
        FREE: 'FREE', // Miễn phí
    },
    // loai tai xe
    FleetDriverType: {
        PERSONAL: 'PERSONAL',            // Cá nhân
        ORGANIZATION: 'ORGANIZATION',    // Thuộc doanh nghiệp
    },
    // hinh thuc lam viec cua tai xe
    FleetDriverWorkingType: {
        OFFICIAL: 'OFFICIAL',            // Chính thức
        COLLABORATOR: 'COLLABORATOR',    // Cộng tác viên
    },
    // trang thai lich su giao dich
    TransactionStatus: {
        PENDING: 'PENDING', // Đang xử lý
        COMPLETED: 'COMPLETED', // Hoàn thành
        CANCELLED: 'CANCELLED', // Đã hủy
        FAILED: 'FAILED', // Thất bại
    },
}

const FleetHelper = {
    // MARK: ---- FILTER ----
    Filter: {
        // danh sach DNVT
        fleetList: {
            page: 0,
            limit: AppConfigs.defaultPageLimit,
            operatorId: '',
            status: '',
            type: '',
            from: getDateFilterOption(dateFilter.tatCaThoiGian)[0].format('YYYY-MM-DD'),
            to: getDateFilterOption(dateFilter.tatCaThoiGian)[1].format('YYYY-MM-DD'),
            selectedValue: dateFilter.tatCaThoiGian,
            areaId: '',
            q: '',
            createdBy: '',
            hasSignedContract: '',
            country: '',
            deepLevel: '',
        },
        // danh sach tai xe
        fleetDriverList: {
            page: 0,
            limit: AppConfigs.defaultPageLimit,
            operatorId: '',
            q: '',
            status: '',
            pricingType: '',
            paymentMethod: '',
            workingType: '',
            agencyId: '',
            from: getDateFilterOption(dateFilter.tatCaThoiGian)[0].format('YYYY-MM-DD'),
            to: getDateFilterOption(dateFilter.tatCaThoiGian)[1].format('YYYY-MM-DD'),
            selectedValue: dateFilter.tatCaThoiGian,
        },
        // danh sach tram sac
        chargeStationList: {
            page: 0,
            limit: AppConfigs.defaultPageLimit,
            status: '',
            operatorId: '',
            q: '',
            managedBy: '',
            agencyId: '',
            landlordId: '',
            areaId: '',
            vehicleType: '',
            type: '',
        },
        // danh sach tru sac
        chargePointList: {
            page: 0,
            limit: AppConfigs.defaultPageLimit,
            q: '',
            status: '',
            type: '',
            stationId: '',
            vehicleType: '',
            chargingSpeed: '',
        },
        // bao cao doi soat
        reconciliationList: {
            page: 0,
            limit: AppConfigs.defaultPageLimit,
            q: '',
            paymentStatus: '',
            month: moment().subtract(1, 'months').format('YYYY-MM'),
        },
        // lich su giao dich
        transactionList: {
            q: '',
            from: getDateFilterOption(dateFilter.baMuoiNgayGanNhat)[0].format('YYYY-MM-DD'),
            to: getDateFilterOption(dateFilter.baMuoiNgayGanNhat)[1].format('YYYY-MM-DD'),
            selectedValue: dateFilter.baMuoiNgayGanNhat,
            status: '',
            page: 0,
            limit: AppConfigs.defaultPageLimit,
            forRole: '',
            type: '',
        }
    },
    // MARK: ---- DATA ----
    Data: {
        fleetTypeOptions: [
            { value: FleetConstants.FleetType.INDIVIDUAL, name: 'Cá nhân' },
            { value: FleetConstants.FleetType.ORGANIZATION, name: 'Tổ chức' },
        ],
        fleetStatusOptions: [
            { value: FleetConstants.FleetStatus.NORMAL, name: 'Đang hoạt động' },
            { value: FleetConstants.FleetStatus.SIGNED, name: 'Đã ký hợp đồng' },
            { value: FleetConstants.FleetStatus.SUSPENDED, name: 'Ngừng hoạt động' },
            { value: FleetConstants.FleetStatus.DELETED, name: 'Đã xoá' },
            { value: FleetConstants.FleetStatus.REGISTERED, name: 'Chờ kích hoạt' },
        ],
        fleetCreatedByOptions: [
            { value: FleetConstants.FleetCreatedBy.ME, name: 'Tài khoản của bạn' },
            { value: FleetConstants.FleetCreatedBy.OTHERS, name: 'Người khác' },
            { value: FleetConstants.FleetCreatedBy.SELF, name: 'Tự đăng ký' },
        ],
        transactionStatusOptions: [
            { value: FleetConstants.TransactionStatus.PENDING, name: 'Đang xử lý' },
            { value: FleetConstants.TransactionStatus.COMPLETED, name: 'Hoàn thành' },
            { value: FleetConstants.TransactionStatus.CANCELLED, name: 'Đã hủy' },
            { value: FleetConstants.TransactionStatus.FAILED, name: 'Thất bại' },
        ],
        paymentMethodOptions: [
            { value: FleetConstants.PaymentMethod.FLEET, name: 'Doanh nghiệp thanh toán' },
            { value: FleetConstants.PaymentMethod.WALLET, name: 'Tài xế thanh toán' },
        ],
        DriverChargingTypeOptions: [
            { value: FleetConstants.PaymentMethod.FLEET, name: 'Doanh nghiệp thanh toán phí sạc' },
            { value: FleetConstants.PaymentMethod.WALLET, name: 'Tài xế thanh toán phí sạc' },
        ],
        paymentMethodTransactionOptions: [
            { value: FleetConstants.PaymentMethod.FLEET, name: 'Doanh nghiệp' },
            { value: FleetConstants.PaymentMethod.WALLET, name: 'Tài xế' },
        ],
        pricingTypeOptions: [
            { value: FleetConstants.PricingType.STANDARD, name: 'Tiêu chuẩn' },
            { value: FleetConstants.PricingType.DISCOUNT, name: 'Ưu đãi' },
            { value: FleetConstants.PricingType.FREE, name: 'Miễn phí' },
        ],
        fleetDriverStatusOptions: [
            { value: FleetConstants.FleetStatus.NORMAL, name: 'Đang hoạt động' },
            { value: FleetConstants.FleetStatus.SUSPENDED, name: 'Ngừng hoạt động' },
            { value: FleetConstants.FleetStatus.DELETED, name: 'Đã xoá' },
            { value: FleetConstants.FleetStatus.REGISTERED, name: 'Chờ kích hoạt' },
        ],
        workingTypeOptions: [
            { value: FleetConstants.FleetDriverWorkingType.OFFICIAL, name: 'Chính thức' },
            { value: FleetConstants.FleetDriverWorkingType.COLLABORATOR, name: 'Cộng tác viên' },
        ],
        creditLimitPeriodOptions: [
            { value: 'DAY', name: 'Ngày' },
            { value: 'MONTH', name: 'Tháng' },
            { value: 'QUARTER', name: 'Quý' },
            { value: 'YEAR', name: 'Năm' },
        ],
    },
    // MARK: ---- API ----
    API: {
        // MARK: ---- DASHBOARD ----
        // thong ke chi so cua DNVT
        requestGetFleetReport: async (params) => {
            const url = '/fleet/dashboard/report';
            try {
                const response = await axiosClient.get(url, { params });
                const { result, totalTopup, totalSpent } = response;
                if (result === 'success') {
                    return { totalTopup, totalSpent };
                }
                return null;
            } catch (error) {
                return null;
            }
        },
        // thong ke cac chi so theo ngay
        requestGetDashboardDaily: async (params) => {
            const url = '/fleet/dashboard/daily';
            try {
                const response = await axiosClient.get(url, { params });
                const { result, daily } = response;
                if (result === 'success') {
                    return daily;
                }
                return null;
            } catch (error) {
                return null;
            }
        },

        // MARK: ---- DNVT ----
        // lay danh sach DNVT
        requestGetFleetList: async (params) => {
            const url = '/fleet';
            try {
                const response = await axiosClient.get(url, { params });
                const { result, fleets, total } = response;
                if (result === 'success') {
                    return { fleets, total };
                }
                return null;
            } catch (error) {
                return null;
            }
        },
        // lay chi tiet DNVT
        requestGetFleetDetail: async (agencyId) => {
            const url = `/fleet/${agencyId}`;
            try {
                const response = await axiosClient.get(url);
                const { result, fleet } = response;
                if (result === 'success') {
                    return fleet;
                }
                return null;
            } catch (error) {
                return error?.response?.data?.reason || null;
            }
        },
        // tao DNVT
        requestCreateFleet: async (params) => {
            const url = '/fleet/create';
            try {
                const response = await axiosClient.post(url, params);
                const { result, fleet } = response;
                if (result === 'success') {
                    return fleet;
                }
                return null;
            } catch (error) {
                return error?.response?.data?.reason || null;
            }
        },
        // cap nhat DNVT
        requestUpdateFleet: async (agencyId, params) => {
            const url = `/fleet/${agencyId}`;
            try {
                const response = await axiosClient.put(url, params);
                const { result, agencyId } = response;
                return result === 'success';
            } catch (error) {
                return error?.response?.data?.reason || null;
            }
        },
        // xoa DNVT
        requestDeleteFleet: async (agencyId) => {
            const url = `/fleet/${agencyId}`;
            try {
                const response = await axiosClient.delete(url);
                const { result } = response;
                return result === 'success';
            } catch (error) {
                return error?.response?.data?.reason || false;
            }
        },
        // doi mat khau (cua nguoi khac)
        requestResetUserPassword: async (params) => {
            const url = `/fleet/resetUserPassword`;
            try {
                const response = await axiosClient.post(url, params);
                const { result } = response;
                return result === 'success';
            } catch (error) {
                return error?.response?.data?.reason || false;
            }
        },
        // dat lai mat khau cua DNVT
        requestResetFleetPassword: async (params) => {
            const url = '/fleet/resetFleetPassword';
            try {
                const response = await axiosClient.post(url, params);
                const { result } = response;
                return result === 'success';
            } catch (error) {
                return error?.response?.data?.reason || false;
            }
        },
        // cap nhat thong tin cua chinh DNVT
        requestFleetUpdateProfile: async (params) => {
            const url = `/fleet/updateProfile`;
            try {
                const response = await axiosClient.put(url, params);
                const { result, userId } = response;
                return result === 'success';
            } catch (error) {
                return error?.response?.data?.reason || null;
            }
        },
        // DNVT nap tien
        requestFleetTopup: async (params) => {
            const url = `/fleet/topup`;
            try {
                const response = await axiosClient.post(url, params);
                const { result, transaction } = response;
                return result === 'success';
            } catch (error) {
                return error?.response?.data?.reason || null;
            }
        },
        // DNVT check trang thai nap tien
        requestFleetCheckPaymentStatus: async (params) => {
            const url = `/fleet/payment`;
            try {
                const response = await axiosClient.post(url, params);
                const { result, payment } = response;
                if (result === 'success') {
                    return payment?.status ?? '';
                }
            } catch (error) {
                return error?.response?.data?.reason || null;
            }
        },
        // lich su giao dich cua DNVT
        requestGetFleetTransactions: async (params) => {
            const url = `/fleet/transactions`;
            try {
                const response = await axiosClient.get(url, { params });
                return response;
            } catch (error) {
                return error?.response?.data?.reason || null;
            }
        },
        // MARK: ---- TÀI XẾ ----
        // lay danh sach tai xe
        requestGetFleetDriverList: async (params) => {
            const url = '/fleet/driver';
            try {
                const response = await axiosClient.get(url, { params });
                const { result, drivers, total } = response;
                if (result === 'success') {
                    return { drivers, total };
                }
                return null;
            } catch (error) {
                return null;
            }
        },
        // lay chi tiet tai xe
        requestGetFleetDriverDetail: async (agencyId) => {
            const url = `/fleet/driver/${agencyId}`;
            try {
                const response = await axiosClient.get(url);
                const { result, driver } = response;
                if (result === 'success') {
                    return driver;
                }
                return null;
            } catch (error) {
                return error?.response?.data?.reason || null;
            }
        },
        // tao tai xe
        requestCreateFleetDriver: async (params) => {
            const url = '/fleet/driver/create';
            try {
                const response = await axiosClient.post(url, params);
                const { result, driver } = response;
                if (result === 'success') {
                    return driver;
                }
                return null;
            } catch (error) {
                return error?.response?.data?.reason || null;
            }
        },
        // cap nhat tai xe
        requestUpdateFleetDriver: async (driverId, params) => {
            const url = `/fleet/driver/${driverId}`;
            try {
                const response = await axiosClient.put(url, params);
                const { result, driverId } = response;
                return result === 'success';
            } catch (error) {
                return error?.response?.data?.reason || null;
            }
        },
        // xoa tai xe
        requestDeleteFleetDriver: async (driverId) => {
            const url = `/fleet/driver/${driverId}`;
            try {
                const response = await axiosClient.delete(url);
                const { result } = response;
                return result === 'success';
            } catch (error) {
                return error?.response?.data?.reason || false;
            }
        },
        // dat lai mat khau cua tai xe
        requestResetFleetDriverPassword: async (params) => {
            const url = '/fleet/driver/resetDriverPassword';
            try {
                const response = await axiosClient.post(url, params);
                const { result } = response;
                return result === 'success';
            } catch (error) {
                return error?.response?.data?.reason || false;
            }
        },
        // lay danh sach xe cua tai xe
        requestGetDriverVehicleList: async (params) => {
            const url = '/fleet/driver/vehicle';
            try {
                const response = await axiosClient.get(url, { params });
                const { result, vehicles, total } = response;
                if (result === 'success') {
                    return { vehicles, total };
                }
                return null;
            } catch (error) {
                return null;
            }
        },
        // lay lich su sac xe cua tai xe
        requestGetDriverChargeSessionsList: async (params) => {
            const url = '/fleet/driver/chargeSessions';
            try {
                const response = await axiosClient.get(url, { params });
                const { result, chargeSessions, total } = response;
                if (result === 'success') {
                    return { chargeSessions, total };
                }
                return null;
            } catch (error) {
                return null;
            }
        },
        // lay lich su giao dich cua tai xe
        requestGetDriverTransactionsList: async (params) => {
            const url = '/fleet/driver/transactions';
            try {
                const response = await axiosClient.get(url, { params });
                const { result, paymentCount, topupCount, transactionCount, payments, topups, transactions } = response;
                if (result === 'success') {
                    return { paymentCount, topupCount, transactionCount, payments, topups, transactions };
                }
                return { paymentCount: 0, topupCount: 0, transactionCount: 0, payments: [], topups: [], transactions: [] };
            } catch (error) {
                return { paymentCount: 0, topupCount: 0, transactionCount: 0, payments: [], topups: [], transactions: [] };
            }
        },
        // MARK: ---- TRAM SAC ----
        // lay danh sach tram sac
        requestGetChargeStationList: async (params) => {
            const url = '/fleet/cs';
            try {
                const response = await axiosClient.get(url, { params });
                const { result, chargeStations, total } = response;
                if (result === 'success') {
                    return { chargeStations, total };
                }
                return { chargeStations: [], total: 0 };
            } catch (error) {
                return { chargeStations: [], total: 0 };
            }
        },
        // lay chi tiet tram sac
        getChargeStationDetail: async (chargeStationId) => {
            const url = `/fleet/cs/${chargeStationId}`;
            try {
                const response = await axiosClient.get(url);
                const { result, chargeStation } = response;
                if (result === 'success') {
                    return chargeStation;
                }
                return null;
            } catch (error) {
                return error?.response?.data?.reason || null;
            }
        },

        // MARK: ---- TRU SAC ----
        // lay danh sach tru sac
        requestGetChargePointList: async (params) => {
            const url = '/fleet/cp';
            try {
                const response = await axiosClient.get(url, {
                    params,
                    baseURL: `${AppConfigs.env.baseUrl}api/v1`
                });
                const { result, chargePoints, total } = response;
                if (result === 'success') {
                    return { chargePoints, total };
                }
                return { chargePoints: [], total: 0 };
            } catch (error) {
                return { chargePoints: [], total: 0 };
            }
        },
        // lay chi tiet tru sac
        requestGetChargePointDetail: async (chargePointId) => {
            const url = `/fleet/cp/${chargePointId}`;
            try {
                const response = await axiosClient.get(url);
                const { result, chargePoint } = response;
                if (result === 'success') {
                    return chargePoint;
                }
                return null;
            } catch (error) {
                return error?.response?.data?.reason || null;
            }
        },
        // MARK: ---- TAI KHOAN NGAN HANG ----
        // lay danh sach tai khoan ngan hang
        requestGetFleetBankList: async (params) => {
            const url = `/fleet/bankAccount/all`;
            try {
                const response = await axiosClient.get(url, { params });
                const { result, bankAccounts } = response;
                if (result === 'success') {
                    return bankAccounts;
                }
                return null;
            } catch (error) {
                return error?.response?.data?.reason || null;
            }
        },
        // tao tai khoan ngan hang
        requestCreateFleetBank: async (params) => {
            const url = '/fleet/bankAccount/create';
            try {
                const response = await axiosClient.post(url, params);
                const { result, bankAccount } = response;
                if (result === 'success') {
                    return bankAccount;
                }
                return null;
            } catch (error) {
                return error?.response?.data?.reason || null;
            }
        },
        // cap nhat tai khoan ngan hang
        requestUpdateFleetBank: async (bankAccountId, params) => {
            const url = `/fleet/bankAccount/${bankAccountId}`;
            try {
                const response = await axiosClient.put(url, params);
                const { result, bankAccountId } = response;
                return result === 'success';
            } catch (error) {
                return error?.response?.data?.reason || null;
            }
        },
        // xoa tai khoan ngan hang
        requestDeleteFleetBank: async (bankAccountId) => {
            const url = `/fleet/bankAccount/${bankAccountId}`;
            try {
                const response = await axiosClient.delete(url);
                const { result } = response;
                return result === 'success';
            } catch (error) {
                return error?.response?.data?.reason || false;
            }
        },
        // MARK: ---- HOP DONG DNVT ----
        // lay ds hop dong cua DNVT
        requestGetFleetContractList: async (params) => {
            const url = '/fleet/contract/all';
            try {
                const response = await axiosClient.get(url, { params });
                const { result, contracts, total } = response;
                if (result === 'success') {
                    return { contracts, total };
                }
                return { contracts: [], total: 0 };
            } catch (error) {
                return { contracts: [], total: 0 };
            }
        },
        // sinh so hop dong
        requestGenerateContractNumber: async (params) => {
            const url = '/fleet/contract/generateContractNumber';
            try {
                const response = await axiosClient.post(url, params);
                const { result, contractNumber } = response;
                if (result === 'success') {
                    return contractNumber;
                }
                return null;
            } catch (error) {
                return error?.response?.data?.reason || null;
            }
        },
        // tao hop dong DNVT
        requestCreateFleetContract: async (params) => {
            const url = '/fleet/contract/createFleetContract';
            try {
                const response = await axiosClient.post(url, params);
                const { result, contract } = response;
                if (result === 'success') {
                    return contract;
                }
                return null;
            } catch (error) {
                return error?.response?.data?.reason || null;
            }
        },
        // admin ky hop dong
        requestAdminSignContract: async (contractId) => {
            const url = `/fleet/contract/adminSignContract/${contractId}`;
            try {
                const response = await axiosClient.post(url, {});
                const { result, contract } = response;
                if (result === 'success') {
                    return contract;
                }
                return null;
            } catch (error) {
                return error?.response?.data?.reason || null;
            }
        },
        // DNVT ky hop dong
        requestFleetSignContract: async (contractId, hsmAgreementUUID, hsmPassword) => {
            const url = `/fleet/contract/agencySignContract/${contractId}`;
            try {
                const response = await axiosClient.post(url, { hsmAgreementUUID, hsmPassword });
                const { result, contract } = response;
                if (result === 'success') {
                    return contract;
                }
                return null;
            } catch (error) {
                return error?.response?.data?.reason || null;
            }
        },
        // cap nhat hop dong
        requestUpdateContract: async (contractId, params) => {
            const url = `/fleet/contract/updateContract/${contractId}`;
            try {
                const response = await axiosClient.put(url, params);
                const { result, contract } = response;
                if (result === 'success') {
                    return contract;
                }
                return null;
            } catch (error) {
                return error?.response?.data?.reason || null;
            }
        },
        // xoa hop dong
        requestDeleteContract: async (contractId) => {
            const url = `/fleet/contract/deleteContract/${contractId}`;
            try {
                const response = await axiosClient.delete(url);
                const { result } = response;
                return result === 'success';
            } catch (error) {
                return error?.response?.data?.reason || false;
            }
        },
        // MARK: ---- ĐỐI SOÁT ----
        // lay ds doi soat DNVT
        requestGetReconciliationList: async (params) => {
            const url = '/fleet/reconciliation/list';
            try {
                const response = await axiosClient.get(url, { params });
                const { result, reconciliations, total, totalAmount } = response;
                if (result === 'success') {
                    return { reconciliations, total, totalAmount };
                }
                return null;
            } catch (error) {
                return error?.response?.data?.reason || null;
            }
        },
        // lay chi tiet doi soat DNVT
        requestGetReconciliationDetail: async (reconciliationId) => {
            const url = `/fleet/reconciliation/detail?reconciliationId=${reconciliationId}`;
            try {
                const response = await axiosClient.get(url);
                const { result, reconciliation } = response;
                if (result === 'success') {
                    return reconciliation;
                }
                return null;
            } catch (error) {
                return error?.response?.data?.reason || null;
            }
        },
        // lay ds doi soat tai xe
        requestGetDriverReconciliationList: async (params) => {
            const url = '/fleet/reconciliation/driver/list';
            try {
                const response = await axiosClient.get(url, { params });
                const { result, reconciliations, total, totalAmount } = response;
                if (result === 'success') {
                    return { reconciliations, total, totalAmount };
                }
                return null;
            } catch (error) {
                return error?.response?.data?.reason || null;
            }
        },
        // lay chi tiet doi soat tai xe
        requestGetDriverReconciliationDetail: async (reconciliationId) => {
            const url = `/fleet/reconciliation/driver/detail?reconciliationId=${reconciliationId}`;
            try {
                const response = await axiosClient.get(url);
                const { result, reconciliation } = response;
                if (result === 'success') {
                    return reconciliation;
                }
                return null;
            } catch (error) {
                return error?.response?.data?.reason || null;
            }
        },
        // tao doi soat DNVT
        requestCreateFleetReconciliation: async (params) => {
            const url = '/fleet/reconciliation/create';
            try {
                const response = await axiosClient.post(url, params);
                const { result, reconciliations } = response;
                return result === 'success';
            } catch (error) {
                return error?.response?.data?.reason || null;
            }
        },
        // gui phan hoi doi soat
        requestCreateFeedbackReconciliation: async (params) => {
            const url = '/fleet/reconciliation/feedback';
            try {
                const response = await axiosClient.post(url, params);
                const { result } = response;
                return result === 'success'
            } catch (error) {
                return error?.response?.data?.reason || null;
            }
        },
    },
    // MARK: ---- UTILS ----
    Utils: {
        // lay mau trang thai DNVT
        getFleetStatusColor: (status) => {
            switch (status) {
                case FleetConstants.FleetStatus.NORMAL:
                    return 'success';
                case FleetConstants.FleetStatus.SIGNED:
                    return 'info';
                case FleetConstants.FleetStatus.SUSPENDED:
                    return 'gray';
                case FleetConstants.FleetStatus.DELETED:
                    return 'danger';
                case FleetConstants.FleetStatus.REGISTERED:
                    return 'info';
                default:
                    return 'warning';
            }
        },
        // lay ten trang thai thanh toan
        getTransactionStatusName: (status) => {
            switch (status) {
                case FleetConstants.TransactionStatus.PENDING:
                    return 'Đang xử lý';
                case FleetConstants.TransactionStatus.COMPLETED:
                    return 'Hoàn thành';
                case FleetConstants.TransactionStatus.CANCELLED:
                    return 'Đã hủy';
                case FleetConstants.TransactionStatus.FAILED:
                    return 'Thất bại';
                default:
                    return '';
            }
        },
        // lay mau trang thai thanh toan
        getTransactionStatusColor: (status) => {
            switch (status) {
                case FleetConstants.TransactionStatus.PENDING:
                    return 'warning';
                case FleetConstants.TransactionStatus.COMPLETED:
                    return 'success';
                case FleetConstants.TransactionStatus.CANCELLED:
                    return 'danger';
                case FleetConstants.TransactionStatus.FAILED:
                    return 'danger';
                default:
                    return '';
            }
        },
        // lay ten loai gia sac
        getChargePricingTypeName: (price) => {
            switch (price) {
                case 0:
                    return 'Tiêu chuẩn';
                case 1:
                    return 'Miễn phí';
                default:
                    return `Ưu đãi`;
            }
        },
        // lay ten loai tai xe
        getFleetDriverTypeName: (type) => {
            switch (type) {
                case FleetConstants.FleetDriverType.PERSONAL:
                    return 'Cá nhân';
                case FleetConstants.FleetDriverType.ORGANIZATION:
                    return 'Thuộc doanh nghiệp';
                default:
                    return '';
            }
        },
        // lay ten hinh thuc lam viec cua tai xe
        getFleetDriverWorkingTypeName: (workingType) => {
            switch (workingType) {
                case FleetConstants.FleetDriverWorkingType.OFFICIAL:
                    return 'Chính thức';
                case FleetConstants.FleetDriverWorkingType.COLLABORATOR:
                    return 'Cộng tác viên';
                default:
                    return '';
            }
        },
        // lay ten chinh sach thanh toan phi sac
        getPaymentMethodName: (method) => {
            switch (method) {
                case FleetConstants.PaymentMethod.WALLET:
                    return 'Tài xế thanh toán';
                case FleetConstants.PaymentMethod.FLEET:
                    return 'Doanh nghiệp thanh toán';
                default:
                    return '';
            }
        },
        // lay ten chinh sach thanh toan phi sac cho phien sac
        getPaymentMethodTransactionName: (method) => {
            switch (method) {
                case FleetConstants.PaymentMethod.WALLET:
                    return 'Tài xế';
                case FleetConstants.PaymentMethod.FLEET:
                    return 'Doanh nghiệp';
                default:
                    return '';
            }
        },
        // lay ten ky han ap dung
        getCreditLimitPeriodName: (date) => {
            switch (date) {
                case 'DAY':
                    return 'Ngày';
                case 'MONTH':
                    return 'Tháng';
                case 'QUARTER':
                    return 'Quý';
                case 'YEAR':
                    return 'Năm';
                default:
                    return '';
            }
        },
    },
    // MARK: ---- UI ----
    UI: {
        // dung hoat dong
        showDialogConfirmStopFleet: (row, onCompleted = () => { }) => {
            AppDialogHelper.show(
                'Ngừng hoạt động',
                `Bạn có chắc muốn ngừng hoạt động DNVT <span class="font-weight-boldest text-gray-800">${row?.name}</span> không?`,
                [
                    {
                        title: 'Hủy bỏ',
                        type: 'neutral',
                        onClick: () => {
                            AppDialogHelper.hide();
                        },
                    },
                    {
                        title: 'Ngừng hoạt động',
                        type: 'negative',
                        onClick: async () => {
                            AppDialogHelper.hide();
                            const res = await FleetHelper.API.requestUpdateFleet(row?.agencyId, {
                                status: 'SUSPENDED',
                            });
                            if (res && _.isBoolean(res)) {
                                ToastHelper.showSuccess(`Ngừng hoạt động DNVT thành công`);
                                onCompleted();
                            } else {
                                ToastHelper.showError(res || `Ngừng hoạt động DNVT thất bại`);
                            }
                        },
                    },
                ],
                false,
                AppResource.images.dialogIcons.dialogStopAgency
            );
        },
        // khoi phuc hoạt dong
        showDialogConfirmActiveFleet: (row, onCompleted = () => { }) => {
            AppDialogHelper.show(
                'Khôi phục hoạt động',
                `Bạn có chắc muốn khôi phục hoạt động DNVT <span class="font-weight-boldest text-gray-800">${row?.name}</span> không?`,
                [
                    {
                        title: 'Hủy bỏ',
                        type: 'neutral',
                        onClick: () => {
                            AppDialogHelper.hide();
                        },
                    },
                    {
                        title: 'Khôi phục',
                        type: 'success',
                        onClick: async () => {
                            AppDialogHelper.hide();
                            const res = await FleetHelper.API.requestUpdateFleet(row?.agencyId, {
                                status: 'NORMAL',
                            });
                            if (res && _.isBoolean(res)) {
                                ToastHelper.showSuccess(`Khôi phục DNVT thành công`);
                                onCompleted();
                            } else {
                                ToastHelper.showError(res || `Khôi phục DNVT thất bại`);
                            }
                        },
                    },
                ],
                false,
                AppResource.images.dialogIcons.dialogActiveAgency
            );
        },
        // xoa DNVT
        showDialogConfirmDeleteFleet: (row, onCompleted = () => { }) => {
            AppDialogHelper.show(
                `Xoá DNVT`,
                `Bạn thực sự muốn xoá DNVT <span class="font-weight-boldest text-gray-800">${row?.name}</span>?`,
                [
                    {
                        title: 'Hủy bỏ',
                        type: 'neutral',
                        onClick: () => {
                            AppDialogHelper.hide();
                        },
                    },
                    {
                        title: 'Xóa',
                        type: 'negative',
                        onClick: async () => {
                            AppDialogHelper.hide();
                            const res = await FleetHelper.API.requestDeleteFleet(row?.agencyId);
                            if (res && _.isBoolean(res)) {
                                ToastHelper.showSuccess(`Xoá DNVT thành công`);
                                onCompleted();
                            } else {
                                ToastHelper.showError(res || `Xóa DNVT thất bại`);
                            }
                        },
                    },
                ],
                false,
                AppResource.images.dialogIcons.dialogDeleteVehicle
            );
        },
        // kich hoat DNVT
        showDialogConfirmAcceptFleet: (row, onCompleted = () => { }) => {
            AppDialogHelper.show(
                `Kích hoạt tài khoản`,
                `Bạn có chắc muốn kích hoạt tài khoản DNVT <span class="font-weight-boldest text-gray-800">${row?.name}</span> không?`,
                [
                    {
                        title: 'Hủy bỏ',
                        type: 'neutral',
                        onClick: () => {
                            AppDialogHelper.hide();
                        },
                    },
                    {
                        title: 'Kích hoạt',
                        type: 'success',
                        onClick: async () => {
                            AppDialogHelper.hide();
                            const isSuccess = await FleetHelper.API.requestUpdateFleet(row?.agencyId, {
                                status: FleetConstants.FleetStatus.NORMAL,
                            });
                            if (isSuccess) {
                                ToastHelper.showSuccess(`Kích hoạt tài khoản DNVT thành công`);
                                onCompleted();
                            } else {
                                ToastHelper.showError(`Kích hoạt tài khoản DNVT thất bại`);
                            }
                        },
                    },
                ],
                false,
                AppResource.images.dialogIcons.dialogAcceptAgency
            );
        },
        // hien dialog xac nhan tao hop dong voi DNVT
        showDialogConfirmCreateContractWithFleet: (fleet, onDone, contract, onCheck) => {
            console.log({ fleet, contract });
            AppCustomDialogHelper.show(
                '',
                <FleetCreateContractView
                    fleet={fleet}
                    contract={contract}
                    title={!Utils.isValid(contract) ? 'Tạo hợp đồng DNVT' : 'Cập nhật hợp đồng DNVT'}
                    contentEl={
                        !Utils.isValid(contract) ? (
                            <>
                                Bạn muốn tạo hợp đồng DNVT cho người dùng {' '}
                                <span style={UIHelper.buildFontStyle('700', '14px', AppColors.gray[600])}>
                                    {fleet?.name}
                                </span>
                                ?
                            </>
                        ) : null
                    }
                    onCancel={() => {
                        AppCustomDialogHelper.hide();
                    }}
                    onConfirm={async (param) => {
                        if (
                            param?.contractType === AgencyConstants.ContractType.DIGITAL &&
                            !fleet?.hsmAgreementUUID
                        ) {
                            ToastHelper.showError(`DNVT chưa có chữ ký số`);
                        } else {
                            if (onCheck) {
                                await onCheck();
                            }
                            let paramCreateContract = {
                                agencyId: fleet?.agencyId,
                                operatorId: fleet?.operatorId,
                                ...param,
                            };
                            let result = null;
                            if (Utils.isValid(contract)) {
                                result = await FleetHelper.API.requestUpdateContract(
                                    contract?.contractId,
                                    paramCreateContract
                                );
                            } else {
                                result = await FleetHelper.API.requestCreateFleetContract(paramCreateContract);
                            }
                            if (result && _.isObject(result)) {
                                ToastHelper.showSuccess(
                                    !Utils.isValid(contract)
                                        ? 'Tạo hợp đồng thành công'
                                        : 'Cập nhật hợp đồng thành công'
                                );
                                if (onDone) {
                                    onDone();
                                }
                                AppCustomDialogHelper.hide();
                            } else {
                                ToastHelper.showError(result);
                                return false;
                            }
                        }
                    }}
                />,
                'md',
                false,
                true
            );
        },
        showDialogConfirmChangeFleetPassword: (fleet, onDone) => {
            AppCustomDialogHelper.show(
                '',
                <AgencySaleDialogView
                    title="Đặt lại mật khẩu"
                    contentEl={<>Thay đổi mật khẩu đăng nhập cho tài khoản DNVT</>}
                    iconEl={<img src={AppResource.images.dialogIcons.dialogChangePassword} />}
                    bodyEl={
                        <ChangeFleetPasswordView
                            fleet={fleet}
                            onCancel={() => {
                                AppCustomDialogHelper.hide();
                            }}
                            onDone={() => {
                                AppCustomDialogHelper.hide();
                                if (onDone) {
                                    onDone();
                                }
                            }}
                        />
                    }
                    removeFooter={true}
                />,
                'md',
                false,
                true
            );
        },
        // hien dialog phan hoi doi soat
        showDialogReconciliationFeedback: (reconciliation, adminRole, onDone) => {
            AppCustomDialogHelper.show(
                '',
                <AgencySaleDialogView
                    iconEl={<img src={AppResource.images.dialogIcons.dialogFeedback} />}
                    title={adminRole ? "Xem phản hồi" : "Gửi phản hồi"}
                    contentEl={
                        adminRole ?
                            <>
                                Xem phản hồi từ đối tác
                            </>
                            : <>
                                Vui lòng nhập lý do gửi phản hồi cho nhà điều hành.
                            </>
                    }
                    bodyEl={
                        <DialogFleetReconciliationFeedback
                            reconciliation={reconciliation}
                            adminRole={adminRole}
                            onCancel={() => {
                                AppCustomDialogHelper.hide();
                            }}
                            onDone={() => {
                                AppCustomDialogHelper.hide();
                                if (onDone) {
                                    onDone();
                                }
                            }}
                        />
                    }
                    removeFooter={true}
                />,
                'md',
                false,
                true
            );
        },
        // hien dialog tao lai doi soat
        showDialogCreateReconciliation: (reconciliation, onDone) => {
            AppCustomDialogHelper.show(
                'Tạo lại đối soát',
                <DialogCreateFleetReconciliation
                    item={reconciliation}
                    onCancel={() => AppCustomDialogHelper.hide()}
                    onDone={() => {
                        onDone();
                        AppCustomDialogHelper.hide();
                    }}
                />,
                'md',
                false,
                true
            );
        },
        // hien dialog thiet lap lich bao tri dinh ky
        showDialogCreateMaintenanceSchedule: (item, onDone) => {
            AppCustomDialogHelper.show(
                'Thiết lập lịch bảo trì định kỳ',
                <DialogAddScheduleMaintenanceView
                    item={item}
                    onCancel={() => AppCustomDialogHelper.hide()}
                    onDone={() => {
                        onDone();
                        AppCustomDialogHelper.hide();
                    }}
                />,
                'lg',
                false,
                true
            );
        },
        // hien dialog nap tien cho DNVT
        showDialogTopUpFleet: (fleet, onDone) => {
            AppCustomDialogHelper.show(
                '',
                <AgencySaleDialogView
                    iconEl={<img src={AppResource.images.dialogIcons.dialogTopUp} />}
                    title="Nạp tiền cho doanh nghiệp"
                    contentEl={
                        <>
                            Nhập số tiền để nạp vào ví của doanh nghiệp:{' '}
                            <span style={UIHelper.buildFontStyle('600', '14px', AppColors.gray[800])}>
                                {fleet?.name}
                            </span>
                        </>
                    }
                    bodyEl={
                        <DialogTopUpFleetView
                            fleet={fleet}
                            onCancel={() => {
                                AppCustomDialogHelper.hide();
                            }}
                            onDone={() => {
                                AppCustomDialogHelper.hide();
                                if (onDone) {
                                    onDone();
                                }
                            }}
                        />
                    }
                    removeFooter={true}
                />,
                'md',
                false,
                true
            );
        },
        // hien dialog dat gia sac xe cho tai xe
        showDialogSetDriverChargingPrice: (driver, onDone) => {
            AppCustomDialogHelper.show(
                '',
                <AgencySaleDialogView
                    iconEl={<img src={AppResource.images.dialogIcons.dialogSetChargingPrice} />}
                    title="Đặt chính sách thanh toán phí sạc"
                    contentEl={
                        <>
                            Chọn loại chính sách sạc được áp dụng cho tài xế mỗi lần sạc xe.
                        </>
                    }
                    bodyEl={
                        <DialogSetDriverChargingPriceView
                            driverDetail={driver}
                            onCancel={() => {
                                AppCustomDialogHelper.hide();
                            }}
                            onDone={() => {
                                AppCustomDialogHelper.hide();
                                if (onDone) {
                                    onDone();
                                }
                            }}
                        />
                    }
                    removeFooter={true}
                />,
                'md',
                false,
                true
            );
        },
    },
}

export default FleetHelper;